import { useState, useMemo, useEffect } from 'react'
import { ItemModelOption } from '../../../types/response'
import { Button } from 'vnetwork-ui'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/components/ui/popover'
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from '@/components/ui/command'
import { X, Loader2, Check, ChevronsUpDown, Trash2 } from 'lucide-react'
import { cn } from '@/lib/utils'

// Data structure for child component in form state
export interface ChildComponentFormData {
    id: string
    model_id: string
    model_label: string
    category: string
    hasTags: 'yes' | 'no'
    serviceTags: string[]
    quantity: string
    condition: string
}

interface ChildComponentCardProps {
    data: ChildComponentFormData
    models: ItemModelOption[]
    isLoadingModels: boolean
    onUpdate: (data: ChildComponentFormData) => void
    onRemove: () => void
}

const CONDITIONS = [
    { value: 'new', label: 'New' },
    { value: 'used', label: 'Used' },
    { value: 'refurbished', label: 'Refurbished' },
]

const ChildComponentCard = ({
    data,
    models,
    isLoadingModels,
    onUpdate,
    onRemove,
}: ChildComponentCardProps) => {
    const [modelPickerOpen, setModelPickerOpen] = useState(false)
    const [modelSearch, setModelSearch] = useState('')
    const [tagInput, setTagInput] = useState('')

    // Filter component models only and group by category
    const filteredModelsByCategory = useMemo(() => {
        const componentModels = models.filter(
            (m) => m.item_type === 'component'
        )
        const searchLower = modelSearch.toLowerCase()
        const filtered = componentModels.filter(
            (m) =>
                m.label.toLowerCase().includes(searchLower) ||
                m.category.toLowerCase().includes(searchLower) ||
                (m.brand?.toLowerCase().includes(searchLower) ?? false)
        )

        const grouped: Record<string, ItemModelOption[]> = {}
        filtered.forEach((model) => {
            const category = model.category || 'OTHER'
            if (!grouped[category]) {
                grouped[category] = []
            }
            grouped[category].push(model)
        })
        return grouped
    }, [models, modelSearch])

    // Get selected model
    const selectedModel = useMemo(() => {
        if (!data.model_id) return null
        return models.find((m) => m.value === data.model_id)
    }, [data.model_id, models])

    // Update model label and category when model changes
    useEffect(() => {
        if (selectedModel && selectedModel.label !== data.model_label) {
            onUpdate({
                ...data,
                model_label: selectedModel.label,
                category: selectedModel.category.toLowerCase(),
            })
        }
    }, [selectedModel])

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Tab' || e.key === 'Enter') {
            e.preventDefault()
            handleAddTags()
        }
    }

    const handleAddTags = () => {
        if (!tagInput.trim()) return
        const newTags = tagInput
            .split(/[\s,\n]+/)
            .map((t) => t.trim())
            .filter((t) => t && !data.serviceTags.includes(t))
        onUpdate({
            ...data,
            serviceTags: [...data.serviceTags, ...newTags],
        })
        setTagInput('')
    }

    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
        e.preventDefault()
        const pastedText = e.clipboardData.getData('text')
        const newTags = pastedText
            .split(/[\s,\n]+/)
            .map((t) => t.trim())
            .filter((t) => t && !data.serviceTags.includes(t))
        onUpdate({
            ...data,
            serviceTags: [...data.serviceTags, ...newTags],
        })
        setTagInput('')
    }

    const handleRemoveTag = (tag: string) => {
        onUpdate({
            ...data,
            serviceTags: data.serviceTags.filter((t) => t !== tag),
        })
    }

    return (
        <Card className="bg-muted/20 border-dashed">
            <CardContent className="pt-4 space-y-3">
                <div className="flex items-start justify-between gap-2">
                    {/* Model Picker */}
                    <div className="flex-1 space-y-2">
                        <Label className="text-xs">
                            Model thành phần{' '}
                            <span className="text-destructive">*</span>
                        </Label>
                        <Popover
                            open={modelPickerOpen}
                            onOpenChange={setModelPickerOpen}
                            modal={false}
                        >
                            <PopoverTrigger asChild>
                                <button
                                    type="button"
                                    role="combobox"
                                    aria-expanded={modelPickerOpen}
                                    disabled={isLoadingModels}
                                    className={cn(
                                        'flex h-9 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm',
                                        'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
                                        'disabled:cursor-not-allowed disabled:opacity-50'
                                    )}
                                >
                                    {isLoadingModels ? (
                                        <div className="flex items-center gap-2">
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                            <span>Loading...</span>
                                        </div>
                                    ) : selectedModel ? (
                                        <span className="truncate">
                                            {selectedModel.label}
                                            {selectedModel.brand &&
                                                ` - ${selectedModel.brand}`}
                                        </span>
                                    ) : (
                                        <span className="text-muted-foreground">
                                            Chọn model...
                                        </span>
                                    )}
                                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                </button>
                            </PopoverTrigger>
                            <PopoverContent
                                className="w-[350px] p-0 z-[60]"
                                align="start"
                            >
                                <Command shouldFilter={false}>
                                    <CommandInput
                                        placeholder="Tìm component..."
                                        value={modelSearch}
                                        onValueChange={setModelSearch}
                                    />
                                    <CommandList className="max-h-[250px]">
                                        {Object.keys(filteredModelsByCategory)
                                            .length === 0 ? (
                                            <CommandEmpty>
                                                Không tìm thấy model.
                                            </CommandEmpty>
                                        ) : (
                                            Object.entries(
                                                filteredModelsByCategory
                                            ).map(
                                                ([
                                                    category,
                                                    categoryModels,
                                                ]) => (
                                                    <CommandGroup
                                                        key={category}
                                                        heading={category}
                                                    >
                                                        {categoryModels.map(
                                                            (model) => (
                                                                <CommandItem
                                                                    key={
                                                                        model.value
                                                                    }
                                                                    value={
                                                                        model.value
                                                                    }
                                                                    onSelect={() => {
                                                                        onUpdate(
                                                                            {
                                                                                ...data,
                                                                                model_id:
                                                                                    model.value,
                                                                                model_label:
                                                                                    model.label,
                                                                                category:
                                                                                    model.category.toLowerCase(),
                                                                            }
                                                                        )
                                                                        setModelPickerOpen(
                                                                            false
                                                                        )
                                                                        setModelSearch(
                                                                            ''
                                                                        )
                                                                    }}
                                                                >
                                                                    <Check
                                                                        className={cn(
                                                                            'mr-2 h-4 w-4',
                                                                            data.model_id ===
                                                                                model.value
                                                                                ? 'opacity-100'
                                                                                : 'opacity-0'
                                                                        )}
                                                                    />
                                                                    <span className="truncate">
                                                                        {
                                                                            model.label
                                                                        }
                                                                        {model.brand &&
                                                                            ` - ${model.brand}`}
                                                                    </span>
                                                                </CommandItem>
                                                            )
                                                        )}
                                                    </CommandGroup>
                                                )
                                            )
                                        )}
                                    </CommandList>
                                </Command>
                            </PopoverContent>
                        </Popover>
                    </div>

                    {/* Condition Selector */}
                    <div className="w-32 space-y-2">
                        <Label className="text-xs">Tình trạng</Label>
                        <Select
                            value={data.condition}
                            onValueChange={(v) =>
                                onUpdate({ ...data, condition: v })
                            }
                        >
                            <SelectTrigger className="h-9">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {CONDITIONS.map((c) => (
                                    <SelectItem key={c.value} value={c.value}>
                                        {c.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Remove Button */}
                    <Button
                        variant="text"
                        color="error"
                        size="small"
                        type="button"
                        onClick={onRemove}
                        className="mt-6"
                    >
                        <Trash2 className="w-4 h-4" />
                    </Button>
                </div>

                {/* Radio: Has serial or quantity */}
                <div className="flex gap-4 text-sm">
                    <label className="flex items-center gap-2 cursor-pointer">
                        <input
                            type="radio"
                            name={`hasTags-${data.id}`}
                            value="yes"
                            checked={data.hasTags === 'yes'}
                            onChange={() =>
                                onUpdate({ ...data, hasTags: 'yes' })
                            }
                            className="w-3 h-3 text-primary"
                        />
                        <span>Có serial</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                        <input
                            type="radio"
                            name={`hasTags-${data.id}`}
                            value="no"
                            checked={data.hasTags === 'no'}
                            onChange={() =>
                                onUpdate({ ...data, hasTags: 'no' })
                            }
                            className="w-3 h-3 text-primary"
                        />
                        <span>Số lượng</span>
                    </label>
                </div>

                {/* Service Tags or Quantity */}
                {data.hasTags === 'yes' ? (
                    <div className="space-y-2">
                        <Input
                            value={tagInput}
                            onChange={(e) => setTagInput(e.target.value)}
                            onKeyDown={handleKeyDown}
                            onPaste={handlePaste}
                            onBlur={handleAddTags}
                            placeholder="Nhập serial number, nhấn Tab để thêm..."
                            className="h-9"
                        />
                        {data.serviceTags.length > 0 && (
                            <div className="flex flex-wrap gap-1">
                                {data.serviceTags.map((tag) => (
                                    <Badge
                                        key={tag}
                                        variant="secondary"
                                        className="gap-1 pr-1 text-xs"
                                    >
                                        {tag}
                                        <button
                                            type="button"
                                            onClick={() => handleRemoveTag(tag)}
                                            className="ml-1 hover:bg-muted rounded-full p-0.5"
                                        >
                                            <X className="w-3 h-3" />
                                        </button>
                                    </Badge>
                                ))}
                            </div>
                        )}
                        <div className="text-xs text-muted-foreground">
                            {data.serviceTags.length} component(s)
                        </div>
                    </div>
                ) : (
                    <div className="space-y-2">
                        <Input
                            type="number"
                            min="1"
                            value={data.quantity}
                            onChange={(e) =>
                                onUpdate({ ...data, quantity: e.target.value })
                            }
                            placeholder="Số lượng..."
                            className="h-9 w-32"
                        />
                        {data.quantity && parseInt(data.quantity) > 0 && (
                            <div className="text-xs text-muted-foreground">
                                {data.quantity} component(s)
                            </div>
                        )}
                    </div>
                )}
            </CardContent>
        </Card>
    )
}

export default ChildComponentCard
